import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@oforce/global-components';

import { useDownloadFile } from '../../hooks';
import { useDownloadFailedDocumentsJobContext } from '../../context';
import { ToastContent } from '../../components/react-toastify';

export default function DownloadFailedDocumentsJob() {
  const { jobId, message, finished, filename, url, error, jobErrors, clearJobId } =
    useDownloadFailedDocumentsJobContext();
  const toastId = useRef(null);

  useEffect(() => {
    if (!toast.current && jobId) {
      toastId.current = toast(<DownloadFailedDocuments />, { autoClose: false, closeOnClick: false });
    }
  }, [toastId, jobId, clearJobId]);

  useEffect(() => {
    if (!toastId.current) {
      return;
    }

    if (error) {
      toast.update(toastId.current, {
        render: () => <ErrorDownloadFailedDocuments error={jobErrors || error} />,
        onClose: clearJobId
      });
    } else if (!finished && !url) {
      toast.update(toastId.current, {
        render: () => <DownloadFailedDocuments message={message} />
      });
    } else if (finished) {
      toast.update(toastId.current, {
        render: () => (
          <DownloadFailedDocumentsDownload
            message={message}
            url={url}
            filename={filename}
            errors={jobErrors}
            clearJobId={clearJobId}
          />
        ),
        onClose: clearJobId
      });
    }
    // eslint-disable-next-line
  }, [toastId, message, filename, url, finished, error, jobErrors]);

  return <></>;
}

const DownloadFailedDocuments = ({ message }) => {
  let title = message ?? 'Preparing Download Failed Documents';

  return (
    <ToastContent
      title={title}
      icon={['far', 'spinner-third']}
      iconProps={{ spin: true }}
      message="Please allow us some time to gather the failed documents."
    />
  );
};

const ErrorDownloadFailedDocuments = ({ error }) => (
  <ToastContent
    title="Error Downloading Failed Documents"
    titleProps={{ className: 'text-danger' }}
    icon={['fas', 'times-circle']}
    message={error.message || error}
  />
);

const DownloadFailedDocumentsDownload = ({ message, url, filename, errors, clearJobId }) => {
  const { download, loading } = useDownloadFile({ url, filename });

  const downloadAndClearJobId = () => {
    download();
    clearJobId();
  };

  return (
    <ToastContent
      title={message || 'Download Failed Documents Ready'}
      titleProps={{ className: 'text-success' }}
      icon={url ? ['fas', 'check-circle'] : ['far', 'exclamation-triangle']}
      message={url && 'Your failed documents are ready for download.'}
    >
      {errors && errors.length > 0 && errors.map(e => <div key={e}>{e}</div>)}
      <div>
        {url && (
          <Button
            color="link"
            size="sm"
            icon={['far', 'arrow-to-bottom']}
            className="mr-2"
            loading={loading}
            loadingText="Downloading..."
            onClick={downloadAndClearJobId}
          >
            Download Now
          </Button>
        )}
      </div>
    </ToastContent>
  );
};
